'use client'
import { useAnimation } from 'framer-motion'
import { useRouter } from 'next/navigation'
import { useEffect, useRef } from 'react'

import useHash from '@/shared/hooks/useHash'
import useHashChange from '@/shared/hooks/useHashChange'
import useScrollPosition from '@/shared/hooks/useScrollPosition'
import type { PanInfo, AnimationControls } from 'framer-motion'

import { SHEET_STATE_TYPE } from '@/enums/detail'
import { HASH_KEY_TYPE } from '@/enums/map'

export interface AnimationSheetViewModel {
  texts: { customClass: React.HTMLAttributes<HTMLDivElement>['className'] }
  states: {
    scrollContainerRef: React.RefObject<HTMLDivElement>
    scrollTop: number
  }
  setters: object
  handlers: {
    handleDragEnd: (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => void
    controls: AnimationControls
    toVariant: (sheetType: SHEET_STATE_TYPE) => string
  }
}

export interface useAnimationSheetViewModelProps {
  sheetType: SHEET_STATE_TYPE
}

const useAnimationSheetViewModel = ({ sheetType }: useAnimationSheetViewModelProps): AnimationSheetViewModel => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const { scrollTop } = useScrollPosition(scrollContainerRef)
  const router = useRouter()
  const { updateHash } = useHash()
  const controls = useAnimation()
  const hashChange = useHashChange(HASH_KEY_TYPE.SHEET)

  const toVariant = (sheetType: SHEET_STATE_TYPE): string => {
    switch (sheetType) {
      case SHEET_STATE_TYPE.CLOSE:
        return 'close'
      case SHEET_STATE_TYPE.HIDDEN:
        return 'hidden'
      case SHEET_STATE_TYPE.VISIBLE:
        return 'visible'
    }
  }

  const handleDragEnd = (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const velocityY = info.velocity.y

    let newSheetState: SHEET_STATE_TYPE | null = null
    if (velocityY > 0) {
      switch (sheetType) {
        case SHEET_STATE_TYPE.VISIBLE:
          if (scrollTop > 0) return
          newSheetState = SHEET_STATE_TYPE.HIDDEN
          break
        case SHEET_STATE_TYPE.HIDDEN:
          newSheetState = SHEET_STATE_TYPE.CLOSE
          break
      }
    } else if (velocityY < 0) {
      switch (sheetType) {
        case SHEET_STATE_TYPE.HIDDEN:
          newSheetState = SHEET_STATE_TYPE.VISIBLE
          break
      }
    }

    if (newSheetState !== null) {
      const pathname = window.location.pathname
      const searchParams = window.location.search
      const hash = updateHash({ sheet: newSheetState })
      router.push(`${pathname}${searchParams}${hash}`)
    }
  }

  useEffect(() => {
    const exec = async () => {
      const variant = toVariant(sheetType)
      await controls.start(variant)
    }

    exec()
  }, [sheetType])

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0
    }
  }, [hashChange])

  const customClass = `absolute bottom-0 w-full bg-010 shadow-[0_-2px_6px_0_rgba(0,0,0,0.1)] ${
    toVariant(sheetType) === 'visible' ? 'rounded-null' : 'rounded-t-xlg'
  }
  ${toVariant(sheetType) === 'visible' ? 'z-200' : 'z-30'}`

  return {
    texts: { customClass },
    states: { scrollContainerRef, scrollTop },
    setters: {},
    handlers: { handleDragEnd, controls, toVariant }
  }
}

export default useAnimationSheetViewModel
